import { useTranslation } from 'next-i18next'
import { useMemo } from 'react'
import { LOCATION_TYPE_MAP } from 'src/constants'
import {
  IConfiguration,
  IConfigurationInput,
  ILocation,
  InputMaybe,
  LocationType,
  Maybe,
  SizeFilter
} from 'src/types/graphql'
import {
  decimalsWhenNeeded,
  mapCanStoreVehicleToRadio,
  numberRounedUpWithoutDecimals
} from 'src/utils'

type IMap = {
  [key in LocationType]?: { label: string; description?: string; icon: string }
}

const EXCLUDED = [
  LocationType.Warehouse,
  LocationType.MiniWinery,
  LocationType.Shed,
  LocationType.House,
  LocationType.Yard,
  LocationType.Attic,
  LocationType.Basement,
  LocationType.Penthouse
]

const UNTOUCHED: { [key in LocationType]?: LocationType } = {
  [LocationType.SelfStorageUnit]: LocationType.SelfStorageUnit,
  [LocationType.Parking]: LocationType.Parking,
  [LocationType.Warehouse]: LocationType.Warehouse,
  [LocationType.Land]: LocationType.Land,
  [LocationType.BodegaCommercial]: LocationType.BodegaCommercial,
  [LocationType.BodegaIndustrial]: LocationType.BodegaIndustrial,
  [LocationType.NaveIndustrial]: LocationType.NaveIndustrial
}

export default function useCommon() {
  const { t } = useTranslation('common')
  const locMap = t('LOCATION_TYPE', { returnObjects: true })
  const inputLocMap = t('INPUT_LOCATION_TYPE', { returnObjects: true })
  const vehicleMap = t('CAN_STORE_VEHICLE', { returnObjects: true })
  const sizeMap = t('SIZE_FILTER', { returnObjects: true })

  const inputLocMapValues = useMemo(() => {
    const inputLocMapValues = Object.entries(inputLocMap).reduce(
      (memo: { [key: string]: string }, [key, value]) => {
        memo[key] = value
        return memo
      },
      {}
    )
    const filteredMap = (
      Object.entries(locMap) as [keyof typeof locMap, string][]
    )
      .filter(([key]) => !EXCLUDED.includes(key as LocationType))
      .reduce((memo: IMap, [key, value]) => {
        const inputLabel = inputLocMapValues[key]
        const match = inputLabel?.match(/\((.+)\)/)
        memo[key] = {
          label: match ? value : (inputLabel ?? value),
          description: match?.[1],
          icon: LOCATION_TYPE_MAP[key]?.icon ?? ''
        }
        return memo
      }, {})
    const locMapValues = Object.entries(filteredMap).reduce(
      (memo: { [key: string]: string }, [key, value]) => {
        memo[key] = value.label
        return memo
      },
      {}
    )

    return {
      filteredMap,
      inputLocMapValues: { ...locMapValues, ...inputLocMapValues }
    }
  }, [inputLocMap, locMap])

  const value = useMemo(() => {
    return {
      filteredMap: inputLocMapValues.filteredMap,
      inputLocMapValues: inputLocMapValues.inputLocMapValues,
      locMap: (location: Maybe<ILocation> | undefined) => {
        if (!location) return ''
        const { defaultConfiguration } = location
        const { canStoreVehicle, size, locationType } =
          defaultConfiguration ?? {}
        const untouched = locationType ? UNTOUCHED[locationType] : undefined
        if (untouched) return locMap[untouched]
        const sqm = (size?.length ?? 1) * (size?.width ?? 1)
        const simplified = canStoreVehicle
          ? LocationType.Parking
          : sqm > 15
            ? LocationType.Warehouse
            : LocationType.SelfStorageUnit
        return locMap[simplified]
      },
      configLocMap: (
        config: Maybe<IConfiguration | IConfigurationInput> | undefined
      ) => {
        if (!config) return ''
        const { canStoreVehicle, size, locationType } = config ?? {}
        const untouched = locationType ? UNTOUCHED[locationType] : undefined
        if (untouched) return locMap[untouched]
        const sqm = (size?.length ?? 1) * (size?.width ?? 1)
        const simplified = canStoreVehicle
          ? LocationType.Parking
          : sqm > 15
            ? LocationType.Warehouse
            : LocationType.SelfStorageUnit
        return locMap[simplified]
      },
      nonSimplifiedLocMap: (locationType: Maybe<LocationType> | undefined) =>
        locationType ? locMap[locationType] : '',

      vehicleMapValues: vehicleMap,
      vehicleMap: (canStoreVehicle: InputMaybe<boolean> | undefined) =>
        vehicleMap[mapCanStoreVehicleToRadio(canStoreVehicle)],

      sizeMapValues: sizeMap,
      sizeMap: (size: Maybe<SizeFilter> | undefined) =>
        size ? sizeMap[size] : '',

      showSize: (
        location: Maybe<ILocation> | undefined,
        alwaysArea?: boolean
      ) => {
        const { defaultConfiguration } = location ?? {}
        const { size } = defaultConfiguration ?? {}
        const area = (size?.length ?? 0) * (size?.width ?? 0)
        if (area > 15 || alwaysArea)
          return numberRounedUpWithoutDecimals(area) + ' m²'
        const length = decimalsWhenNeeded(size?.length ?? 0)
        const width = decimalsWhenNeeded(size?.width ?? 0)
        return `${length} x ${width}`
      },
      getSizeConfig: (
        config: IConfiguration | IConfigurationInput,
        alwaysArea?: boolean
      ) => {
        const { size } = config ?? {}
        const area = (size?.length ?? 0) * (size?.width ?? 0)
        if (area > 15 || alwaysArea)
          return numberRounedUpWithoutDecimals(area) + ' m²'
        const length = decimalsWhenNeeded(size?.length ?? 0)
        const width = decimalsWhenNeeded(size?.width ?? 0)
        return `${length} x ${width}`
      },
      sortConfigs: (
        a: IConfiguration | IConfigurationInput,
        b: IConfiguration | IConfigurationInput
      ) => {
        if (a.canStoreVehicle && !b.canStoreVehicle) return -1
        if (!a.canStoreVehicle && b.canStoreVehicle) return 1
        const aSqm = (a.size?.length ?? 0) * (a.size?.width ?? 0)
        const bSqm = (b.size?.length ?? 0) * (b.size?.width ?? 0)
        return aSqm - bSqm
      }
    }
  }, [vehicleMap, sizeMap, locMap, inputLocMapValues])

  return value
}
